import { INCENTIVE_VALUES } from 'site-modules/shared/constants/inventory/price-analysis';
import { get, isFinite, sumBy } from 'lodash-es';

import { getCashByTermsRange } from 'site-modules/shared/utils/car-buying/get-cash-by-terms-range';
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';

const LEASE_COMBINABLE_LEVEL = 4;
const DEFAULT_ARRAY = [];

/**
 * Checks if subprogram is compatible with checked incentives.
 * @param {array} checkedIncentives
 * @param {number} subprogramId
 * @returns {boolean}
 */
export function isSubprogramCompatible(checkedIncentives, subprogramId) {
  return checkedIncentives.every(({ compatibleSubprogramIds = [] }) => compatibleSubprogramIds.includes(subprogramId));
}

/**
 * Returns sum of passed items by passed names.
 * @param {String} name - set value which invokes for each element in received array.
 * @return {Function} - new predefined function which sums objects using name's values.
 *
 * Example:
 * const f = getSumBy('age', 'age1');
 * f([{ age: 10 }, { age: 15 }, { age1: 20 }]) => 45;
 */
export const getSumBy =
  (...names) =>
  items =>
    sumBy(items, item => Number(item[names.find(name => item[name])]) || 0);

/**
 * Returns sum of passed incentives.
 * @param {object[]} incentives
 * @returns {number}
 */
export function getIncentivesSum(incentives) {
  return getSumBy(
    INCENTIVE_VALUES.AMOUNT,
    INCENTIVE_VALUES.REBATE,
    INCENTIVE_VALUES.CASH,
    INCENTIVE_VALUES.TAX_REBATE_AMOUNT
  )(incentives);
}

/**
 * Merges primary incentives with checked and removes some of them if it's have incompatible subprogram.
 * @param {object[]} checkedIncentives
 * @param {object[]} primaryIncentives
 * @returns {{appliedIncentives: object[], primaryIncentiveAmount: number, conditionalIncentiveAmount: number}}
 */
export function mergePrimaryWithChecked(checkedIncentives = DEFAULT_ARRAY, primaryIncentives = DEFAULT_ARRAY) {
  const filteredPrimaryIncentives = checkedIncentives.length
    ? primaryIncentives.filter(
        ({ subprogramId, primary }) => primary && isSubprogramCompatible(checkedIncentives, subprogramId)
      )
    : primaryIncentives;

  return {
    primaryIncentiveAmount: getIncentivesSum(filteredPrimaryIncentives),
    conditionalIncentiveAmount: getIncentivesSum(checkedIncentives),
    appliedIncentives: [...filteredPrimaryIncentives, ...checkedIncentives],
    checkedIncentives,
  };
}

/**
 * Filter out lease incentives.
 * @param {object[]} incentives
 */
export function filterOutLeaseIncentives(incentives) {
  return incentives.filter(({ combinableLevel }) => combinableLevel !== LEASE_COMBINABLE_LEVEL);
}

/**
 * Creates a replacer for array items matched by field.
 * @param {string} field - Field used to match objects.
 * @returns {function} A function accepting initial and search arrays.
 */
export const replaceBy = field => (array, searchArray) =>
  /**
   * @param {object[]} array - Initial array where each item is checked for replacement.
   * @param {object[]} searchArray - Values used as replacements when field matches.
   * @returns {object[]} New collection with replaced values.
   */
  array.map(value => searchArray.find(searchValue => value[field] === searchValue[field]) || value);

/**
 * Returns true/false whenever some of passed checkedIncentives eq. passed incentiveId.
 * @param {array} checkedIncentives
 * @param {number} incentiveId
 * @returns {boolean}
 */
export function isIncentiveChecked(checkedIncentives, incentiveId) {
  return checkedIncentives.some(({ id }) => id === incentiveId);
}

export function getDealerPrice(inventory) {
  const advertisedPrice = get(inventory, 'prices.advertisedPrice', 0);
  const docFee = get(inventory, 'prices.docFee');
  const hasDocFee = isFinite(docFee) && docFee >= 0;
  return hasDocFee ? advertisedPrice + docFee : advertisedPrice;
}

export function filterOutNoCacheIncentives(incentives = []) {
  return incentives.filter(incentive => getCashByTermsRange(incentive) !== 0);
}

export const isTmvPriceAvailable = vinData => !!vinData && (isUsed(vinData.type) || vinData.prices?.tmv);
